/**
 * Feature gating's first layer as pure data.
 *
 * A tenant's entitlement is **computed, never stored**
 * ([ADR-0023](../../../docs/decisions/0023-entitlements-are-deltas-over-a-code-defined-tier-bundle.md)):
 * the tier bundles live here, `catalog.tenants.entitlements_json` holds only the
 * exceptions, and nothing derived is written to a row. So a tier change has
 * nothing to re-derive, and adding a feature to a bundle reaches every school on
 * that tier the moment the code ships.
 *
 * This module deliberately imports nothing. `apps/admin` renders "this needs
 * Groove" from the same table the server gates on, and telling a client what to
 * *offer* is not telling it what to *allow* — the boundary is unaffected.
 */
/**
 * Every feature this platform can gate. A **closed union**: an unknown key is
 * rejected at write time and ignored at read time, so a typo can never become a
 * permission.
 *
 * Keys come from [`business-plan.md`](../../../docs/product/business-plan.md)
 * §Tier detail and each is a durable commercial capability, which is one of the
 * two statuses [ADR-0009](../../../docs/decisions/0009-three-layer-feature-gating.md)
 * requires — the other being a rollout gate with a removal date. A key with
 * neither is debt.
 *
 * Caps are **not** keys. Member, staff and location limits and course storage
 * quotas are quantities, not surfaces; they belong to `resource_limits_json` and
 * to post-MVP tier-caps enforcement.
 */
export const FEATURE_KEYS = [
    // Starter — every school gets these.
    'scheduling',
    'payments',
    'checkin',
    'crm_basic',
    'dashboard',
    // Pulse.
    'shop',
    'automations_basic',
    'reporting',
    'courses',
    // Groove.
    'crm_advanced',
    'automations_advanced',
    'reporting_advanced',
    'multi_location',
    'branding',
    'custom_domain',
    // Studio.
    'api_access',
];
/**
 * The commercial plans, cheapest first. Order is load-bearing: the monotonicity
 * test walks this array pairwise.
 */
export const TIERS = ['starter', 'pulse', 'groove', 'studio', 'custom'];
/**
 * What each tier includes, **written out in full**.
 *
 * Composing a bundle by spreading the one below (`[...PULSE, 'branding']`) would
 * read better and would make the monotonicity test unable to fail — it would be
 * asserting a property of the spread operator rather than of this table. A test
 * that cannot fail is worse than no test, because it reports coverage it does
 * not have.
 *
 * `custom` equals `studio` on purpose: Custom buys unlimited caps and bespoke
 * terms, neither of which is a feature key. A one-off capability for a Custom
 * school is a `grants` entry, which is what the deltas exist for.
 */
export const TIER_BUNDLES = Object.freeze({
    starter: Object.freeze([
        'scheduling',
        'payments',
        'checkin',
        'crm_basic',
        'dashboard',
    ]),
    pulse: Object.freeze([
        'scheduling',
        'payments',
        'checkin',
        'crm_basic',
        'dashboard',
        'shop',
        'automations_basic',
        'reporting',
        'courses',
    ]),
    groove: Object.freeze([
        'scheduling',
        'payments',
        'checkin',
        'crm_basic',
        'dashboard',
        'shop',
        'automations_basic',
        'reporting',
        'courses',
        'crm_advanced',
        'automations_advanced',
        'reporting_advanced',
        'multi_location',
        'branding',
        'custom_domain',
    ]),
    studio: Object.freeze([
        'scheduling',
        'payments',
        'checkin',
        'crm_basic',
        'dashboard',
        'shop',
        'automations_basic',
        'reporting',
        'courses',
        'crm_advanced',
        'automations_advanced',
        'reporting_advanced',
        'multi_location',
        'branding',
        'custom_domain',
        'api_access',
    ]),
    custom: Object.freeze([
        'scheduling',
        'payments',
        'checkin',
        'crm_basic',
        'dashboard',
        'shop',
        'automations_basic',
        'reporting',
        'courses',
        'crm_advanced',
        'automations_advanced',
        'reporting_advanced',
        'multi_location',
        'branding',
        'custom_domain',
        'api_access',
    ]),
});
export function isFeatureKey(value) {
    return FEATURE_KEYS.includes(value);
}
export function isTier(value) {
    return TIERS.includes(value);
}
/**
 * Keeps only recognised feature keys out of whatever a `jsonb` column or a
 * request body actually held. Dropped rather than passed through, for the same
 * reason `toTenantRoles` drops an unknown role: a key this build does not know
 * about cannot have been granted by it.
 */
export function toFeatureKeys(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((entry) => typeof entry === 'string' && isFeatureKey(entry));
}
export function parseEntitlementDeltas(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return { grants: [], revokes: [] };
    }
    const raw = value;
    return { grants: toFeatureKeys(raw.grants), revokes: toFeatureKeys(raw.revokes) };
}
/**
 * `feature_flags_json` as the set of keys the owner has switched **on**.
 *
 * A key present and false is off, and a key absent is off — one meaning, two
 * spellings, which is what a `Set` of the true ones collapses.
 */
export function parseFeatureFlags(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return new Set();
    const flags = new Set();
    for (const [key, enabled] of Object.entries(value)) {
        if (enabled === true && isFeatureKey(key))
            flags.add(key);
    }
    return flags;
}
/**
 * ADR-0023's formula: `entitled(f) = (f ∈ bundle(tier) ∪ grants) ∧ f ∉ revokes`.
 *
 * `revokes` is applied last and wins outright — over a grant and over the tier.
 * A revocation is a contractual or legal removal, and the one thing it must
 * survive is a tier upgrade; if the tier won, upgrading a school would silently
 * restore a capability someone deliberately took away.
 *
 * An unrecognised tier resolves to the **starter** bundle rather than throwing.
 * The column is an unconstrained varchar, so a bad value is possible; failing
 * closed to the smallest bundle keeps it from becoming an upgrade, and throwing
 * would turn one bad row into a 500 on every request that tenant makes.
 */
export function resolveEntitled(tier, deltas) {
    const bundle = isTier(tier) ? TIER_BUNDLES[tier] : TIER_BUNDLES.starter;
    const entitled = new Set([...bundle, ...deltas.grants]);
    for (const revoked of deltas.revokes)
        entitled.delete(revoked);
    return entitled;
}
//# sourceMappingURL=features.js.map